import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch } from "@fortawesome/free-solid-svg-icons";
import { constants } from "utils";
import { getGradeLabel, isAssignableTarget, buildingTowardOrTier1, lessonPrereqCodes } from "utils/standards";
import { logError, LogCategory } from "utils/sentryLogger";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  useCurriculumCourses,
  useCurriculumUnits,
  useLessonDetail,
  useLessonQuestions,
  useNoQuestionTracks,
  useClassroomLessonAssignment,
  changeLesson,
  archiveLesson,
  assignLesson,
} from "hooks/useCurriculum/useCurriculum";
import { useStandards } from "hooks/useStandards/useStandards";
import { StandardsFallback } from "components/shared/StandardsFallback/StandardsFallback";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { LessonDetailPanel } from "components/pages/LessonsStandards/LessonDetailPanel";
import { EMPTY_QUESTION_FILTER, toTrackQuestionFilters, customizedTrackTypes } from "components/pages/LessonsStandards/questionFilters";
import { useQuestionFacets } from "hooks/useQuestionFacets/useQuestionFacets";
import { LessonSidebarTree } from "components/pages/LessonsStandards/LessonSidebarTree";
import { StandardNavigationTree } from "components/pages/GroupCodeStandardSearch/StandardNavigationTree";
import { StandardDetailView } from "components/pages/GroupCodeStandardSearch/StandardDetailView";
import { SearchResultsView } from "components/pages/GroupCodeStandardSearch/SearchResultsView";
import { AssignGroupsDistributionModal } from "components/pages/Educator/Dashboard/AssignGroupsDistributionModal";
import { AssignedCodesBanner } from "components/shared/AssignedCodesBanner/AssignedCodesBanner";
import "components/pages/LessonsStandards/lessonsStandards.css";
import "components/pages/GroupCodeStandardSearch/groupCodeStandardSearch.css";
import logo from "assets/OKO_Core_Logotype2.svg";
import arrow_left_circle from "assets/arrow-left-circle.svg";
import { trackClickedButton } from "utils/analytics";
import { SIDEBAR_BUTTONS, SIDEBAR_MODE_LABELS, sidebarGradeValue, useSidebarTracking } from "hooks/useSidebarTracking";
import { withPanelViewMemory } from "hooks/usePageViewTracker";

const SaveBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 388px;
  right: 0;
  background: white;
  border-top: 1px solid #EAEAFC;
  box-shadow: 0 -1px 1.5px rgba(0, 0, 0, 0.05);
  padding: 16px 32px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  z-index: 10;
`;

const CancelButton = styled.button`
  background: white;
  border: 1px solid #605DEC;
  color: #605DEC;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #F4F4FD; }
`;

const SaveButton = styled.button`
  background: #605DEC;
  border: none;
  color: white;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  &:disabled { background: #ABB1BA; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #4F4DC2; }
`;

const Instruction = styled.div`
  padding: 16px 32px 0;
  font-size: 16px;
  color: #002E19;
  & strong { display: block; margin-bottom: 4px; }
  & p { margin: 0; color: #6B6967; font-size: 14px; }
`;

// Locate a leaf standard (+ its grade/domain/cluster) in the nested standards tree.
const findStandardByCode = (standards, code) => {
  if (!code) return null;
  for (const gradeLevel of standards || []) {
    for (const domain of gradeLevel.domains || []) {
      for (const cluster of domain.clusters || []) {
        for (const std of cluster.standards || []) {
          if (std.code === code) return { ...std, grade: gradeLevel.grade, domain, cluster };
        }
      }
    }
  }
  return null;
};

// Grade / domain / cluster nodes to expand so a code is revealed in the tree.
const computeExpandedPath = (code) => {
  const expanded = {};
  if (!code || !code.includes(".")) return expanded;
  const parts = code.split(".");
  const grade = parts[0].startsWith("HS") ? "HS" : parts[0];
  expanded[grade] = true;
  if (parts.length >= 2) expanded[`${parts[0]}.${parts[1]}`] = true;
  if (parts.length >= 3) {
    const clusterPart = parts[2].split("-")[0];
    expanded[`${parts[0]}.${parts[1]}.${clusterPart.charAt(0)}`] = true;
  }
  return expanded;
};

const ViewChangeLessonPage = () => {
  const history = useHistory();
  const trackSidebar = useSidebarTracking();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const classroomId = urlParams.get("classroomId");
  const assignmentId = urlParams.get("assignmentId");
  const currentLessonId = urlParams.get("currentLessonId");
  // Present when entered from a standard bundle — opens the view in Standard mode.
  const currentStandardCode = urlParams.get("currentStandardCode");
  const groupCodes = urlParams.get("groupCodes")?.split(",").filter(Boolean) || [];
  const urlAvailableCodes = urlParams.get("availableCodes")?.split(",").filter(Boolean) || [];

  // Source the distributable codes from the classroom's group-assignments so the
  // bundle's own codes AND the free pool are always present — in the API's
  // canonical case — regardless of which entry point navigated here. Relying on a
  // URL param alone is fragile: a caller can omit it (e.g. Assign New Lesson) or a
  // stale build can drop it, and the save then falls back to only the currently
  // assigned codes, which 400s the moment the distribution grows past them.
  const { assignment: classroomAssignment } = useClassroomLessonAssignment(classroomId);
  const fetchedBundle = classroomAssignment.lessons.find((b) => b.assignment_id === assignmentId);
  const fetchedBundleCodes = (fetchedBundle?.assignments || []).map((a) => a.group_code).filter(Boolean);
  // The bundle's current codes: prefer the fetched (canonical) set, else the URL.
  const bundleCodes = fetchedBundleCodes.length ? fetchedBundleCodes : groupCodes;
  // Bundle codes first (so growing keeps them, shrinking drops pool codes), then
  // the free pool. While the fetch is in flight, fall back to the URL param, then
  // to the bundle's own codes.
  const fetchedAvailable = [...new Set([...bundleCodes, ...(classroomAssignment.unassigned_codes || [])])];
  const availableCodes = fetchedAvailable.length
    ? fetchedAvailable
    : (urlAvailableCodes.length ? urlAvailableCodes : groupCodes);
  // Single source for the "N of M Groups Selected" ceiling: exactly the codes we
  // can slice and send. Because the modal caps each stepper so the total can never
  // exceed this, `group_codes.length >= sum(distribution)` holds by construction —
  // the modal can't promise more groups than there are codes to back them.
  const totalAvailable = availableCodes.length;
  // Current per-track distribution of the bundle being changed — seeds the save
  // modal. The modal clamps each track to the new lesson's availability (freeing
  // codes whose track the new lesson lacks).
  const initialCounts = {
    core: parseInt(urlParams.get("initCore"), 10) || 0,
    prerequisites: parseInt(urlParams.get("initPrereq"), 10) || 0,
    extensions: parseInt(urlParams.get("initExt"), 10) || 0,
  };

  const [curriculumId, setCurriculumId] = useState(null);
  const [className, setClassName] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadEducator = async () => {
      try {
        const data = await request("account/educator", { method: "GET" });
        if (!cancelled) {
          setCurriculumId(data?.config?.curriculum_id || null);
          setClassName(data?.classes?.find((c) => c.id === classroomId)?.name || null);
        }
      } catch {
        /* noop */
      }
    };
    loadEducator();
    return () => { cancelled = true; };
  }, [classroomId]);

  const { courses } = useCurriculumCourses(curriculumId);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [expandedUnits, setExpandedUnits] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [isDistributionModalOpen, setIsDistributionModalOpen] = useState(false);
  const [error, setError] = useState(null);

  // --- Advanced Selection (question filters), always-on in the edit view ---
  // Per (track_type, standard_code) filter state, keyed `${trackType}|${code ?? ""}`,
  // mirroring the change-lesson payload. Reset when the previewed lesson changes.
  const [filters, setFilters] = useState({});
  useEffect(() => { setFilters({}); }, [selectedLessonId]);
  const getFilter = useCallback(
    (trackType, standardCode) => filters[`${trackType}|${standardCode || ""}`] || EMPTY_QUESTION_FILTER,
    [filters],
  );
  const setFilter = useCallback((trackType, standardCode, next) => {
    setFilters((prev) => ({ ...prev, [`${trackType}|${standardCode || ""}`]: next }));
  }, []);
  const coreFilter = filters["core|"] || EMPTY_QUESTION_FILTER;
  const coreQueryFilter = {
    standards: coreFilter.standards,
    learningComponents: coreFilter.learningComponents,
    questionType: coreFilter.questionType,
  };
  const buildQuestionFilters = () => toTrackQuestionFilters(filters);

  const { units } = useCurriculumUnits(selectedCourseId);

  const { activeModule, activeUnitOrdinal } = useMemo(() => {
    if (!units || !selectedLessonId) return { activeModule: null, activeUnitOrdinal: null };
    for (const unit of units) {
      if (unit.modules) {
        const found = unit.modules.find((m) =>
          m.lessons?.some((l) => l.id === selectedLessonId)
        );
        if (found) return { activeModule: found, activeUnitOrdinal: unit.ordinal_name };
      }
    }
    return { activeModule: null, activeUnitOrdinal: null };
  }, [units, selectedLessonId]);
  const { lesson: currentLesson } = useLessonDetail(currentLessonId);
  const { lesson: previewLesson, loading: previewLoading } = useLessonDetail(selectedLessonId);
  // Core carousel reflects the (always-visible) Core filter.
  const { questions: previewQuestions, total: previewQuestionsTotal, loading: questionsLoading, loadMore: loadMorePreviewQuestions } =
    useLessonQuestions(selectedLessonId, coreQueryFilter);
  // Facets only fetch when a lesson is previewed (null lessonId → the hook no-ops),
  // which is exactly the lesson-mode case here.
  const {
    facets: coreFacets,
    hasLoaded: coreFacetsLoaded,
    error: coreFacetsError,
  } = useQuestionFacets(
    selectedLessonId,
    { standardCode: null, ...coreQueryFilter },
  );

  useEffect(() => {
    if (selectedCourseId || courses.length === 0) return;
    if (currentLessonId) {
      // Lesson entry: wait for the current lesson, then match its course.
      if (currentLesson) {
        const course = courses.find(
          (c) => c.name === currentLesson.course_name || c.grade_level === currentLesson.course_grade_level,
        );
        if (course) setSelectedCourseId(course.id);
      }
      return;
    }
    // Standard entry (no current lesson): default to the first course so the
    // lesson tree is populated if the educator toggles to Lesson mode.
    setSelectedCourseId(courses[0].id);
  }, [currentLessonId, currentLesson, courses, selectedCourseId]);

  useEffect(() => {
    if (currentLessonId && !selectedLessonId) setSelectedLessonId(currentLessonId);
  }, [currentLessonId, selectedLessonId]);

  useEffect(() => {
    if (!selectedLessonId || units.length === 0) return;
    for (const unit of units) {
      if (unit.lessons?.some((l) => l.id === selectedLessonId)) {
        setExpandedUnits((prev) => (prev[unit.id] ? prev : { ...prev, [unit.id]: true }));
        const matchingModule = unit.modules?.find((m) =>
          m.lessons?.some((l) => l.id === selectedLessonId)
        );
        if (matchingModule) {
          setExpandedModules((prev) => (prev[matchingModule.id] ? prev : { ...prev, [matchingModule.id]: true }));
        }
        break;
      }
    }
  }, [selectedLessonId, units]);

  // ---- Standard mode (Lesson/Standard toggle) — mirrors LessonsStandards ----
  // Entered from a standard bundle → open in Standard mode; else Lesson mode.
  const [mode, setMode] = useState(currentStandardCode ? "standard" : "lesson");
  const switchMode = (next) => {
    if (next !== mode) trackSidebar(SIDEBAR_BUTTONS.MODE_TOGGLE, { mode: SIDEBAR_MODE_LABELS[next] });
    setMode(next);
  };
  const standardsData = useStandards(true);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [selectedRelationships, setSelectedRelationships] = useState(null);
  const {
    query: standardSearchQuery,
    results: standardSearchResults,
    showResults: showStandardSearchResults,
    handleInputChange: handleStandardSearchInputChange,
    handleSubmit: handleStandardSearchSubmit,
    clearSearch: clearStandardSearch,
    inputRef: standardSearchInputRef,
  } = useSemanticStandardSearch();

  useEffect(() => {
    if (!selectedStandard?.code || selectedStandard.type) {
      setSelectedRelationships(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await request(`standards/${selectedStandard.code}/relationships`, { method: "GET" });
        if (!cancelled) setSelectedRelationships(data);
      } catch (e) {
        if (!cancelled) setSelectedRelationships(null);
        logError("ViewChangeLesson: failed to load standard relationships", e, {
          category: LogCategory.ERROR,
          standardCode: selectedStandard.code,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStandard?.code, selectedStandard?.type]);

  // Preselect the current standard when entered from a standard bundle.
  useEffect(() => {
    if (!currentStandardCode || selectedStandard || !standardsData || standardsData.length === 0) return;
    const std = findStandardByCode(standardsData, currentStandardCode);
    if (std) {
      setSelectedStandard(std);
      setExpandedNodes(computeExpandedPath(currentStandardCode));
      if (std.grade) setSelectedGrade(std.grade);
    }
  }, [currentStandardCode, selectedStandard, standardsData]);

  const handleToggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const handleSelectStandard = (entity, grade, domain, cluster) => {
    setSelectedStandard({ ...entity, grade, domain, cluster });
    // Selecting opens the node and its path, matching the lesson tree and the
    // quick-group picker — a clicked domain should reveal what is inside it.
    setExpandedNodes((prev) => ({
      ...prev,
      ...(grade ? { [grade]: true } : {}),
      ...(domain?.code ? { [domain.code]: true } : {}),
      ...(cluster?.code ? { [cluster.code]: true } : {}),
      ...(entity?.code ? { [entity.code]: true } : {}),
    }));
    if (showStandardSearchResults) clearStandardSearch();
  };

  const handleGradeChange = (e) => {
    const grade = e.target.value;
    trackSidebar(SIDEBAR_BUTTONS.GRADE_FILTER, { grade: sidebarGradeValue(grade) });
    setSelectedGrade(grade);
    if (grade !== "all") setExpandedNodes((prev) => ({ ...prev, [grade]: true }));
  };

  const availableGrades = (standardsData || [])
    .filter((gradeLevel) => {
      const visibleDomains = (gradeLevel.domains || []).filter((domain) => {
        if (domain.count === 0 || domain.challenge_count === 0) return false;
        const visibleClusters = (domain.clusters || []).filter((cluster) => {
          if (cluster.count === 0 || cluster.challenge_count === 0) return false;
          return (cluster.standards || []).some((std) => std.count !== 0 && std.challenge_count !== 0);
        });
        return visibleClusters.length > 0;
      });
      return visibleDomains.length > 0;
    })
    .map((gradeLevel) => gradeLevel.grade);

  const filteredStandards = selectedGrade === "all"
    ? (standardsData || [])
    : (standardsData || []).filter((g) => g.grade === selectedGrade);

  const standardHasPrereqs = (selectedRelationships?.prerequisites || []).some((g) => g.standards && g.standards.length > 0);
  const standardHasExtensions = (selectedRelationships?.extensions || []).some((g) => g.standards && g.standards.length > 0);

  // Tracks whose backing standard(s) have no questions to assign — disabled in the
  // distribution modal so the educator can't send groups to an empty level. Computed
  // from the same question call each flow's detail panel uses (not the global tree
  // count, which disagrees with lesson-scoped availability).
  const noQuestionTracks = useNoQuestionTracks(
    mode === "standard"
      ? {
        flow: "standard",
        coreCodes: selectedStandard?.code ? [selectedStandard.code] : [],
        prereqCodes: (selectedRelationships?.prerequisites || []).flatMap((g) => (g.standards || []).map((s) => s.code)),
        extCodes: (selectedRelationships?.extensions || []).flatMap((g) => (g.standards || []).map((s) => s.code)),
      }
      : {
        flow: "lesson",
        lessonId: selectedLessonId,
        coreCodes: (previewLesson?.addressing || []).map((a) => a.standard_code).filter(Boolean),
        prereqCodes: lessonPrereqCodes(previewLesson),
        extCodes: buildingTowardOrTier1(previewLesson).map((b) => b.standard_code).filter(Boolean),
      },
  );

  // Swap this bundle to a standard: change the primary standard, then realize the
  // chosen distribution over the selected codes (mirrors ViewChangeStandard).
  const commitStandardEdit = async (counts) => {
    if (!classroomId || !selectedStandard?.code || !assignmentId) return;
    await request(
      `groups/classrooms/${classroomId}/assignments/${assignmentId}/standard`,
      { method: "PUT", payload: { new_standard_code: selectedStandard.code } },
    );
    // Realize the distribution over the selected codes: keep the bundle's current
    // codes and pull enough pool codes to reach the chosen total, so a standard
    // bundle can grow past its current codes (but a smaller total never drops an
    // existing code — the trailing null-track loop keeps it, as before). The
    // full-replace PUT below sets the bundle to exactly these codes.
    const total = counts.core + counts.prerequisites + counts.extensions;
    const codes = availableCodes.slice(0, Math.max(total, bundleCodes.length));
    const prereqStandards = (selectedRelationships?.prerequisites || []).flatMap((g) => g.standards || []).map((s) => s.code).filter(Boolean);
    const extStandards = (selectedRelationships?.extensions || []).flatMap((g) => g.standards || []).map((s) => s.code).filter(Boolean);
    const assignments = [];
    let i = 0;
    for (let n = 0; n < counts.core && i < codes.length; n += 1, i += 1) {
      assignments.push({ group_code: codes[i], track_type: "core", standard_code: selectedStandard.code });
    }
    for (let n = 0; n < counts.prerequisites && i < codes.length && prereqStandards.length; n += 1, i += 1) {
      assignments.push({ group_code: codes[i], track_type: "prerequisites", standard_code: prereqStandards[n % prereqStandards.length] });
    }
    for (let n = 0; n < counts.extensions && i < codes.length && extStandards.length; n += 1, i += 1) {
      assignments.push({ group_code: codes[i], track_type: "extensions", standard_code: extStandards[n % extStandards.length] });
    }
    for (; i < codes.length; i += 1) {
      assignments.push({ group_code: codes[i], track_type: null, standard_code: null });
    }
    if (assignments.length > 0) {
      await request(
        `groups/classrooms/${classroomId}/assignments/${assignmentId}`,
        { method: "PUT", payload: { assignments } },
      );
    }
  };

  const hasChanged = mode === "standard"
    // Clusters and domains are valid targets too; only whole grades are not.
    ? (isAssignableTarget(selectedStandard) && selectedStandard.code !== currentStandardCode)
    : (selectedLessonId && selectedLessonId !== currentLessonId);

  const handleCourseChange = (e) => {
    const course = courses.find((entry) => entry.id === e.target.value);
    trackSidebar(SIDEBAR_BUTTONS.COURSE_FILTER, { course_id: e.target.value, course_name: course?.name });
    setSelectedCourseId(e.target.value);
    setSelectedLessonId(null);
    setExpandedUnits({});
    setExpandedModules({});
  };

  const handleToggleUnit = (unitId) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
  };

  const handleToggleModule = (moduleId) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const handleSelectLesson = (lessonId) => {
    setSelectedLessonId(lessonId);
  };

  const handleSave = () => {
    const isStandardMode = mode === "standard";
    // Literal names and full property objects rather than a template string or
    // a spread, so the tracked-values inventory can be checked by reading the code.
    if (isStandardMode) {
      trackClickedButton("Save Standard", {
        page_name: "Change Lesson",
        assignment_type: "Standard",
        class: classroomId,
        class_name: className,
        standard_id: selectedStandard?.code || null,
      });
    } else {
      trackClickedButton("Save Lesson", {
        page_name: "Change Lesson",
        assignment_type: "Lesson",
        class: classroomId,
        class_name: className,
        standard_id: (previewLesson?.addressing || [])[0]?.standard_code || null,
      });
    }
    if (hasChanged) setIsDistributionModalOpen(true);
  };

  const handleCancel = () => {
    history.push(`${constants.EDUCATOR_PATH}?classId=${classroomId}`);
  };

  // The banner reflects what the bundle *currently* is (the entry point), not the
  // active toggle: standard bundle → "Current Standard", lesson bundle → "Current Lesson".
  const enteredFromStandard = !!currentStandardCode;
  const currentStandardEntity = enteredFromStandard ? findStandardByCode(standardsData, currentStandardCode) : null;
  const bannerLabel = enteredFromStandard ? "Current Standard" : "Current Lesson";
  const bannerValue = enteredFromStandard
    ? `${currentStandardCode}${currentStandardEntity?.name ? ` ${currentStandardEntity.name}` : ""}`
    : (currentLesson
      ? `${currentLesson.course_name}, ${currentLesson.unit_ordinal_name}, ${currentLesson.ordinal_name}: ${currentLesson.name}`
      : null);

  // The bundle's current type (from the entry point) vs the target `mode` (toggle).
  const currentType = enteredFromStandard ? "standard" : "lesson";

  // The DELETE can race with the request helper's retry (the resource is already
  // gone on the retry → 404). Treat 404 as "already deleted" so a raced retry
  // doesn't abort the follow-up assign or surface a false-positive error.
  const deleteBundleTolerant = async (del) => {
    try {
      await del();
    } catch (e) {
      if (e?.status !== 404) throw e;
    }
  };

  // Cross-type change: the single edit endpoints reject editing the other type on an
  // existing assignment, so delete the current bundle (frees its codes to the pool)
  // then assign the new type, which distributes the freed codes from the pool by
  // counts. If the assign fails after the delete, the codes stay freed in the pool
  // (the modal surfaces the error and does not navigate).
  const convertLessonToStandard = async (counts) => {
    await deleteBundleTolerant(() => archiveLesson(classroomId, assignmentId));
    await request("groups/assign", {
      method: "POST",
      payload: { classroom_id: classroomId, standard_code: selectedStandard.code, distribution: counts },
    });
  };
  const convertStandardToLesson = async (counts) => {
    await deleteBundleTolerant(() => request(`groups/classrooms/${classroomId}/assignments/${assignmentId}`, { method: "DELETE" }));
    await assignLesson(classroomId, selectedLessonId, counts, buildQuestionFilters());
  };

  // Save dispatcher: same-type keeps the existing edit; cross-type does delete+assign.
  const commit = async (counts) => {
    if (mode === currentType) {
      if (mode === "standard") return commitStandardEdit(counts);
      // Send the codes the bundle should run over: its current codes plus enough
      // pool codes to reach the chosen total, so the bundle can grow past its
      // current codes. Omitting them keeps only the old codes and 400s when the
      // distribution total exceeds them. A smaller total keeps all current codes
      // (rule 1 allows sum(distribution) < group_codes.length), matching the prior
      // omit-group_codes behavior.
      const total = counts.core + counts.prerequisites + counts.extensions;
      const codes = availableCodes.slice(0, Math.max(total, bundleCodes.length));
      return changeLesson(classroomId, assignmentId, selectedLessonId, counts, codes, buildQuestionFilters());
    }
    return mode === "standard"
      ? convertLessonToStandard(counts)
      : convertStandardToLesson(counts);
  };

  return (
    <div className="lessons-standards">
      <div className="ls-sidebar ls-sidebar--picker">
        <div className="ls-sidebar-header">
          <img src={logo} alt="OKO" className="ls-sidebar-logo" />
          <button
            className="ls-back-button"
            onClick={() => {
              trackSidebar(SIDEBAR_BUTTONS.BACK_TO_GROUPS);
              handleCancel();
            }}
          >
            <img src={arrow_left_circle} alt="back" style={{ width: 20 }} />
            Return to Groups
          </button>
          <div className="ls-toggle-container">
            <button
              className={`ls-toggle-button ${mode === "lesson" ? "active" : ""}`}
              onClick={() => switchMode("lesson")}
            >
              Lesson
            </button>
            <button
              className={`ls-toggle-button ${mode === "standard" ? "active" : ""}`}
              onClick={() => switchMode("standard")}
            >
              Standard
            </button>
          </div>
          {mode === "standard" ? (
            <select
              className="ls-grade-dropdown"
              aria-label="Grade"
              value={selectedGrade}
              onChange={handleGradeChange}
            >
              <option value="all">All Grades</option>
              {availableGrades.map((grade) => (
                <option key={grade} value={grade}>{getGradeLabel(grade)}</option>
              ))}
            </select>
          ) : (
            <select
              className="ls-grade-dropdown"
              value={selectedCourseId || ""}
              onChange={handleCourseChange}
            >
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="ls-sidebar-content">
          {mode === "standard" ? (
            !standardsData || standardsData.length === 0 ? (
              <div className="ls-loading">
                <StandardsFallback />
              </div>
            ) : (
              <StandardNavigationTree
                standards={filteredStandards}
                selectedStandard={selectedStandard}
                expandedNodes={expandedNodes}
                onToggleNode={handleToggleNode}
                onSelectStandard={handleSelectStandard}
              />
            )
          ) : (
            <LessonSidebarTree
              units={units}
              selectedLessonId={selectedLessonId}
              onSelectLesson={handleSelectLesson}
              expandedUnits={expandedUnits}
              onToggleUnit={handleToggleUnit}
              expandedModules={expandedModules}
              onToggleModule={handleToggleModule}
            />
          )}
        </div>
      </div>

      <div className="ls-main-content" style={{ paddingBottom: 96 }}>
        <div className="ls-content-header">
          <h1 className="ls-header-title">View &amp; Change {mode === "standard" ? "Standard" : "Lesson"}</h1>
          {mode === "standard" && (
            <div className="ls-header-search">
              <span className="ls-header-search-icon">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <input
                ref={standardSearchInputRef}
                className="ls-header-search-input"
                placeholder={'Try: "Multiplying Fractions" or "Proportional relationships"'}
                value={standardSearchQuery}
                onChange={handleStandardSearchInputChange}
                onKeyDown={(e) => { if (e.key === "Enter") handleStandardSearchSubmit(); }}
              />
            </div>
          )}
        </div>

        <AssignedCodesBanner
          groupCodes={groupCodes}
          label={bannerLabel}
          value={bannerValue}
        />

        <Instruction>
          <strong>Select the {mode === "standard" ? "standard" : "lesson"} you want to switch to.</strong>
          <p>
            When you save your selection, group assignments will update automatically.
            You can update them anytime from the Groups page.
          </p>
        </Instruction>

        <div className="ls-scrollable-content">
          {mode === "standard" ? (
            !standardsData || standardsData.length === 0 ? (
              <div className="ls-loading">
                <StandardsFallback size="2x" />
              </div>
            ) : showStandardSearchResults ? (
              <SearchResultsView
                searchResults={standardSearchResults || []}
                onResultClick={handleSelectStandard}
                onBackClick={clearStandardSearch}
              />
            ) : selectedStandard ? (
              <StandardDetailView
                standard={selectedStandard}
                groupCode={null}
                onStandardClick={handleSelectStandard}
                pickerMode
              />
            ) : (
              <div className="ls-no-selection">
                Select a standard from the sidebar to view details.
              </div>
            )
          ) : previewLoading ? (
            <div className="ls-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          ) : previewLesson ? (
            <LessonDetailPanel
              lesson={previewLesson}
              module={activeModule}
              unitOrdinal={activeUnitOrdinal}
              questions={previewQuestions}
              questionsLoading={questionsLoading}
              questionsTotal={previewQuestionsTotal}
              onLoadMoreQuestions={loadMorePreviewQuestions}
              showAssignButton={false}
              enableQuestionFilter
              // Edit view: filters are always shown (no "Filter Questions" toggle).
              filterOpen
              onToggleFilter={() => {}}
              getFilter={getFilter}
              setFilter={setFilter}
              coreFacets={coreFacets}
              coreFacetsLoaded={coreFacetsLoaded}
              coreFacetsError={coreFacetsError}
            />
          ) : (
            <div className="ls-no-selection">
              Select a lesson from the sidebar to view details.
            </div>
          )}
        </div>
      </div>

      <SaveBar>
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
        <SaveButton disabled={!hasChanged} onClick={handleSave}>
          Save {mode === "standard" ? "Standard" : "Lesson"}
        </SaveButton>
      </SaveBar>

      {/* Same distribution modal as first-time assign. On submit we swap the
          bundle's lesson (assignment-scoped change-lesson) or its standard
          (per-assignment standard PUT + distribution), per the active mode. */}
      <AssignGroupsDistributionModal
        isOpen={isDistributionModalOpen}
        onClose={() => setIsDistributionModalOpen(false)}
        classroomId={classroomId}
        standardCode={mode === "standard"
          ? (selectedStandard?.code || null)
          : (previewLesson?.addressing?.[0]?.standard_code || null)}
        standardName={mode === "standard"
          ? (selectedStandard?.name || null)
          : (previewLesson?.name || null)}
        infoSubtitle={mode === "standard"
          ? null
          : (previewLesson
            ? [previewLesson.course_name, previewLesson.unit_ordinal_name, previewLesson.ordinal_name].filter(Boolean).join(", ")
            : null)}
        infoTitle={mode === "standard" ? (selectedStandard?.name || null) : (previewLesson?.name || null)}
        totalAvailable={totalAvailable}
        initialCounts={initialCounts}
        hasCore={mode === "standard" ? true : (previewLesson?.addressing?.length || 0) > 0}
        hasPrerequisites={mode === "standard" ? standardHasPrereqs : lessonPrereqCodes(previewLesson).length > 0}
        hasExtensions={mode === "standard" ? standardHasExtensions : buildingTowardOrTier1(previewLesson).length > 0}
        noQuestionTracks={noQuestionTracks}
        customizedTracks={mode === "lesson" ? Array.from(customizedTrackTypes(filters)) : []}
        onSubmit={commit}
        useUnifiedPool={mode === "lesson"}
        onSaved={() => {
          setIsDistributionModalOpen(false);
          history.push(`${constants.EDUCATOR_PATH}?classId=${classroomId}`);
        }}
      />

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          style={{ position: "fixed", bottom: 16, right: 16, zIndex: 9999, maxWidth: 400 }}
        >
          {error}
        </Alert>
      )}
    </div>
  );
};

export const ViewChangeLesson = withPanelViewMemory(ViewChangeLessonPage);
